import React, { useCallback, useState } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { validateField } from '../../utils/validation/validation';
import InputField from '../../utils/InputField';
import { faPlus, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import ListGroup from 'react-bootstrap/ListGroup';
import Dropzone from "../../utils/dropzone";
import GoogleMapsWrapper from '../../utils/GoogleMapsWrapper'; 
import PlaceComponent from '../Address/AddressAutoPop';
import { useAuth } from '../../contexts/AuthContext';

const ContractorForm = React.memo(({ 
    data, 
    contractors, 
    onChange, 
    onAddOrUpdateContractor, 
    onSelectContractor, 
    onValidate, 
    errors, 
    buttonText, 
    isFrom = 'page',
    disabled = false
}) => {
    const { role } = useAuth();
    const isAdmin = role === "Admin";
    const [selectedOption, setSelectedOption] = useState('uploadPDF');
    
    const handleChange = useCallback((field, value) => {
        if (disabled) return;
        onChange(field, value);
        // Skip address validation when in modal mode to allow address to be visible
        if (isFrom === 'modal' && field === 'address') {
            onValidate(field, null); // Clear any errors for address in modal
        } else {
            const error = validateField(field, value);
            onValidate(field, error);
        }
    }, [onChange, onValidate, isFrom, isAdmin, disabled]);

    const handleFileUpload = useCallback((fieldName, uploadedFiles) => {
        if (disabled) return;
        console.log('Files uploaded to field:', fieldName, uploadedFiles);
        onChange(fieldName, uploadedFiles); 
    }, [onChange, disabled]);

    const handleOptionChange = (e) => {
        if (disabled) return;
        setSelectedOption(e.target.value);
    };

    return (
        <Container className="px-0" fluid>
            <Row className="no-gutters upper-wrap">
                <Col sm={isFrom === 'page' ? 4 : 12 } xs="12">
                    <div className="form-wrapper rounded mb-4">
                        <div className="form-heading mb-4 text-uppercase fw-bold">
                            Contractor
                        </div>
                        {contractors?.length > 0 && (
                            <ListGroup as="ol">
                                {contractors.map((contractor, index) => (
                                    <ListGroup.Item
                                        as="li"
                                        className="d-flex justify-content-between align-items-center mb-3 contractor-list"
                                        key={index}
                                        action 
                                        onClick={() => onSelectContractor(index)}
                                    >
                                        <div className="ms-2 me-auto">
                                            {contractor.individualName}
                                        </div>
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                        {!disabled && (
                            <button onClick={onAddOrUpdateContractor} type="button" className="btn btn-next mt-3">
                                <FontAwesomeIcon icon={faPlus} /> {buttonText}
                            </button>
                        )}
                    </div>
                </Col>
                <Col sm={isFrom === 'page' ? 8 : 12 } xs="12">
                    <div className="form-wrapper rounded">
                        <div className="form-heading mb-4 text-uppercase fw-bold">
                            Contractor Information
                        </div>
                        <Row className="mb-3">
                            <InputField
                                field="individualName"
                                label="Company or Individual Name"
                                placeholder="Ex. Jane Doe"                                    
                                value={data.individualName}
                                onChange={handleChange}
                                error={errors.individualName}
                                as={Col}
                                controlId="contractor"
                                disabled={disabled}
                            />
                        </Row>
                        <Row className="mb-3">
                            <InputField
                                field="trade"
                                label="Applicable Trade(s)"
                                value={data.trade}
                                onChange={handleChange}
                                error={errors.trade}
                                as={Col}
                                controlId="contractor"
                                disabled={disabled}
                            />
                        </Row>
                        <Row className="mb-3">
                            <InputField
                                field="email"
                                type="email"
                                label="Village Email"
                                value={data.email || "commercialpermits@pingreegrove.org"}
                                onChange={handleChange}
                                error={errors.email}
                                as={Col}
                                controlId="contractor"
                                disabled={disabled || !isAdmin}
                            />
                        </Row>
                        <Row className="mb-3">
                            <InputField
                                field="phoneNumber"
                                label="Contractor Phone Number"
                                value={data.phoneNumber}
                                onChange={handleChange}
                                error={errors.phoneNumber}
                                as={Col}
                                controlId="contractor"
                                formatPhone={true}
                                disabled={disabled}
                            />
                        </Row>
                        <Row className="mb-3">
                            <GoogleMapsWrapper>
                                <PlaceComponent
                                    field="address"
                                    label="Contractor Address"
                                    className="mb-3"
                                    value={data.address || ""}
                                    onChange={handleChange}
                                    type="text"
                                    error={errors.address}
                                    controlId="contractor"
                                    disabled={disabled}
                                />
                            </GoogleMapsWrapper> 
                        </Row>
                        <Row className="mb-3">
                            <InputField
                                field="contractorEmail"
                                type="email"
                                label="Contractor Email"
                                value={data.contractorEmail || ""}
                                onChange={handleChange}
                                error={errors.contractorEmail}
                                as={Col}
                                controlId="contractor"
                                disabled={disabled}
                            />
                        </Row>
                        {!disabled && (
                            <>
                                <div className="form-heading mt-5 text-uppercase fw-bold">
                                    Contractor Documents & Contractor License (Only PDF)
                                </div>
                                <div className='mt-3 d-flex align-items-center' style={{ gap: '20px' }}>
                                    <label className="d-flex align-items-center mb-0" style={{ cursor: 'pointer', gap: '8px' }}>
                                        <input
                                            type="radio"
                                            value="uploadPDF"
                                            checked={selectedOption === 'uploadPDF'}
                                            onChange={handleOptionChange}
                                            style={{ margin: 0, cursor: 'pointer' }}
                                        />
                                        <span>Upload PDF</span>
                                    </label>
                                    <label className="d-flex align-items-center mb-0" style={{ cursor: 'pointer', gap: '8px' }}>
                                        <input
                                            type="radio"
                                            value="pdfUrl"
                                            checked={selectedOption === 'pdfUrl'}
                                            onChange={handleOptionChange}
                                            style={{ margin: 0, cursor: 'pointer' }}
                                        />
                                        <span>Paste Your URL</span>
                                    </label>
                                </div>
                                <div>
                                    {selectedOption === 'uploadPDF' && (
                                        <div>
                                            <Dropzone 
                                                fileTypes={['application/pdf']} 
                                                showPreview={true} 
                                                fieldName="license" 
                                                onFileUpload={handleFileUpload}
                                                maxFileSizeMB={250}
                                            />
                                        </div>
                                    )}
                                    {selectedOption === 'pdfUrl' && (
                                        <div className='my-3'>
                                            <Row className="mb-3">
                                                <InputField
                                                    field="licenseUrl"
                                                    label="Paste Your URL"
                                                    value={data.licenseUrl || ''}
                                                    onChange={handleChange}
                                                    as={Col}
                                                    controlId="contractor"
                                                />
                                            </Row>
                                            <div className="my-2">
                                                Please enter a valid PDF URL with https:// or http:// .
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        {(isFrom !== 'page' && data.licenseLink?.length > 0) && (
                            <Row>
                                <Col sm="12">
                                    <div className="form-wrapper rounded mt-2">
                                        <div className="form-heading text-uppercase fw-bold">
                                            Download/View License & Proposal
                                        </div>
                                        <ListGroup as="ul">
                                            {data?.licenseLink.map((file, index) => (
                                                <ListGroup.Item as="li" key={index}>
                                                    <a href={file.link} target="_blank" rel="noopener noreferrer">
                                                        File {index + 1}
                                                    </a>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
});;

export default ContractorForm;
